import React from "react";
import { Button, ButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import styled from "styled-components";
import Bulb from "react-bulb";

import { fetchInterval, apiVersionTC, configAgencyName } from "../../config";
import clickIcon from "../../assets/icons/clickIcon.svg";
import falseIcon from "../../assets/icons/falseIcon.svg";
import trueIcon from "../../assets/icons/trueIcon.svg";
import { useAppSelector } from "../../redux/hooks";
import { convertToDateFormat, dateConverter, daysUntil } from "../../utils/helpers";
import variableColors from "../../_App.module.scss";
import useAgencyData from "../../hooks/useAgencyData";

const {
  indigoColor,
  warningColor,
  secondaryColor,
  appBackgroundColor,
  infoColor,
  customTertiaryColor,
  textColor,
  customSecondaryColor,
  primaryColor,
  successColor,
  tealColor,
  darkColor,
  dangerColor,
  appBackgroundColorWhiteSmoke,
} = variableColors;

const CalendarDiv = styled.div`
  border: 1px solid ${secondaryColor};
`;

const CalendarLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  align-content: center;
  &.add-border-bottom {
    border-bottom: 1px dotted ${darkColor};
  }
`;

const HR = styled.hr`
  border-top: 2px solid ${darkColor};
  margin: 0.5rem auto;
`;

const BulbContainer = styled.div`
  padding-left: 0.4rem;
  padding-right: 0.4rem;
  width: auto;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: 0.4rem;
  background-color: inherit;
  margin-right: -0.6rem;
`;

const BulbDiv = styled.div`
  background-color: inherit;
  svg {
    defs {
      display: none !important;
    }
  }
`;

export const TooltipInfoDiv = styled.div<{ $backgroundColor: string; $color: string }>`
  padding: 4px;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$color};
`;

const ButtonsLegend = ({ agencyId }: { agencyId: string }): JSX.Element => {
  const { t } = useTranslation();

  const [busesFromRedux, currentCalendarFromRedux, mapSettingsFromRedux]: [
    {
      busesWithLayover: BusInfo[];
      workingBuses: number;
      busesNumber: number;
      busesOnTime: BusInfo[];
      busesTooFast: BusInfo[];
      delayedBuses: BusInfo[];
      remainingBuses: BusInfo[];
    },
    CurrentCalendar[],
    MapSettings,
  ] = useAppSelector(
    (state: RootState) => [state?.buses, state?.constantValues?.currentCalendar, state?.constantValues?.mapSettings],
    shallowEqual
  );

  //* Custom Hook
  const { feedInfo }: AgencyContextType = useAgencyData() || ({} as AgencyContextType);
  const [feedStartDate, feedEndDate, feedVersionFromHook] = feedInfo || [];

  const [currentCalendarState, setCurrentCalendarState] = React.useState<CurrentCalendar[] | null>(null);
  const [feedVersion, setFeedVersion] = React.useState<string>("");

  React.useEffect(() => {
    if (currentCalendarFromRedux) {
      setCurrentCalendarState(currentCalendarFromRedux);
    }
  }, [currentCalendarFromRedux]);

  React.useEffect(() => {
    if (feedVersionFromHook) {
      setFeedVersion(feedVersionFromHook);
      return () => {
        setFeedVersion("");
      };
    }
  }, [feedVersionFromHook]);

  //* CurrentCalendars render
  const currentCalendars: JSX.Element[] | null =
    currentCalendarState &&
    currentCalendarState!.map((currentCalendar: CurrentCalendar, index: number): JSX.Element => {
      return (
        <CalendarDiv key={index}>
          {`${t("Kalendarz")}:`}{" "}
          <span className="span_bold" style={{ color: indigoColor }}>
            {currentCalendar?.serviceId || t("b/d")}
          </span>{" "}
          <span className="span_bold" style={{ float: "right", color: secondaryColor, marginLeft: "0.25rem" }}>{`${
            index + 1
          }/${currentCalendarState.length}`}</span>
          <CalendarLine>
            {t("Ważny od")}:{" "}
            {currentCalendar && currentCalendar?.startDate ? (
              <span className="span_bold">{dateConverter(currentCalendar?.startDate as string)}</span>
            ) : (
              t("b/d")
            )}
          </CalendarLine>
          <CalendarLine>
            {t("Ważny do")}:{" "}
            {currentCalendar && currentCalendar?.endDate ? (
              <span className="span_bold">{dateConverter(currentCalendar?.endDate as string)}</span>
            ) : (
              t("b/d")
            )}
          </CalendarLine>
          <HR />
          <CalendarLine className="add-border-bottom">
            {t("Monday")}:{" "}
            <img src={currentCalendar?.monday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
          <CalendarLine className="add-border-bottom">
            {t("Tuesday")}:{" "}
            <img src={currentCalendar?.tuesday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
          <CalendarLine className="add-border-bottom">
            {t("Wednesday")}:{" "}
            <img src={currentCalendar?.wednesday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
          <CalendarLine className="add-border-bottom">
            {t("Thursday")}:{" "}
            <img src={currentCalendar?.thursday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
          <CalendarLine className="add-border-bottom">
            {t("Friday")}:{" "}
            <img src={currentCalendar?.friday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
          <CalendarLine className="add-border-bottom">
            {t("Saturday")}:{" "}
            <img src={currentCalendar?.saturday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
          <CalendarLine>
            {t("Sunday")}:{" "}
            <img src={currentCalendar?.sunday ? trueIcon : falseIcon} alt="false/true icon" width={20} height={20} />
          </CalendarLine>
        </CalendarDiv>
      );
    });

  const buttonData = [
    {
      value: (
        <span>
          {t("Suma")}
          {": "}
          <span className="span_bold">{busesFromRedux?.workingBuses}</span>
          {" + "}
          <span className="span_bold">{busesFromRedux?.remainingBuses?.length}</span>
          {" = "}
          <span className="span_bold">{busesFromRedux?.busesNumber}</span>
        </span>
      ),
      color: darkColor,
      backgroundColor: appBackgroundColor,
      infoHeader: `${t("Podsumowanie")}:`,
      infoBody: (
        <React.Fragment>
          <span>
            {`${t("Przypisane autobusy")}:`} <span className="span_bold">{busesFromRedux?.workingBuses}</span>
          </span>
          <br />
          <span>
            {`${t("Nieprzypisane autobusy")}:`} <span className="span_bold">{busesFromRedux?.remainingBuses?.length}</span>
          </span>
          <br />
          <span>
            {`${t("Suma")}:`} <span className="span_bold">{busesFromRedux?.busesNumber}</span>
          </span>
        </React.Fragment>
      ),
    },
    {
      value: (
        <span>
          {`${t("Interwał")}:`} <span className="span_bold">{`${fetchInterval / 1000} s`}</span>{" "}
          {process.env.NODE_ENV === "development" ? (
            <span className="span_bold" style={{ color: warningColor }}>
              /Dev/{apiVersionTC}/{agencyId}/
            </span>
          ) : null}
        </span>
      ),
      color: darkColor,
      backgroundColor: infoColor,
      infoHeader: `${t("Interwał")}:`,
      infoBody: (
        <span>
          {`${t("Pobieranie danych co")}:`}{" "}
          <span className="span_bold" style={{ color: warningColor }}>{`${fetchInterval / 1000} s`}</span>
        </span>
      ),
    },
    {
      value: (
        <span>
          {`${t("FeedStart")}:`} <span className="span_bold">{convertToDateFormat(feedStartDate) || t("b/d")}</span>
        </span>
      ),
      color: appBackgroundColorWhiteSmoke,
      backgroundColor: primaryColor,
      infoHeader: `${t("FeedStart")}:`,
      infoBody: (
        <span>
          {`${t("Dane ważne od")}:`}{" "}
          <span className="span_bold" style={{ color: warningColor }}>
            {convertToDateFormat(feedStartDate) || t("b/d")}
          </span>
        </span>
      ),
    },
    {
      value: (
        <span>
          {`${t("FeedEnd")}:`} <span className="span_bold">{convertToDateFormat(feedEndDate) || t("b/d")}</span>
        </span>
      ),
      color:
        daysUntil(convertToDateFormat(feedEndDate)) > 0 && daysUntil(convertToDateFormat(feedEndDate)) <= 7
          ? darkColor
          : appBackgroundColorWhiteSmoke,
      backgroundColor:
        daysUntil(convertToDateFormat(feedEndDate)) > 7
          ? successColor
          : daysUntil(convertToDateFormat(feedEndDate)) > 0 && daysUntil(convertToDateFormat(feedEndDate)) <= 7
            ? warningColor
            : dangerColor,
      infoHeader: `${t("FeedEnd")}:`,
      infoBody: (
        <span>
          {`${t("Dane ważne do")}:`}{" "}
          <span className="span_bold" style={{ color: "inherit" }}>
            {convertToDateFormat(feedEndDate) || t("b/d")}
          </span>
          <br />
          {feedEndDate ? (
            <span>
              {`${t("Pozostało")}:`}{" "}
              <span className="span_bold" style={{ color: "inherit" }}>
                {daysUntil(convertToDateFormat(feedEndDate))}
              </span>{" "}
              {t("dni")}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      value: (
        <span>
          {feedVersion ? "Feed: " : `${t("Kalendarz")}: `}
          <span className="span_bold">
            {feedVersion
              ? feedVersion
              : currentCalendarState && currentCalendarState?.length === 1
                ? currentCalendarState[0]?.serviceId
                : currentCalendarState && currentCalendarState?.length >= 2
                  ? `[${currentCalendarState?.length}]`
                  : t("b/d")}
          </span>
        </span>
      ),
      color: darkColor,
      backgroundColor: customTertiaryColor,
      infoHeader: `${t("Bieżący kalendarz")}:`,
      infoBody: <React.Fragment>{currentCalendars}</React.Fragment>,
    },
  ];

  const legendButtons: JSX.Element[] = buttonData.map((button, index): JSX.Element => {
    return (
      <OverlayTrigger
        trigger={"click"}
        rootClose={true}
        key={button.backgroundColor + index}
        delay={0}
        placement={"bottom"}
        overlay={
          <Tooltip id={`buttonTimeLegend-${index}`} className="button-tooltip">
            <TooltipInfoDiv $backgroundColor={button.backgroundColor} $color={button.color}>
              <h6>
                <strong>{button.infoHeader}</strong>
              </h6>
              {button.infoBody}
              {index === buttonData.length - 1 ? (
                <span style={{ color: darkColor }}>
                  {t("Ilość")}:{" "}
                  <span className="span_bold" style={{ color: indigoColor }}>
                    {currentCalendarState?.length}
                  </span>
                </span>
              ) : null}
            </TooltipInfoDiv>
          </Tooltip>
        }
      >
        <Button
          key={button.backgroundColor + index}
          size="sm"
          style={{
            backgroundColor: button.backgroundColor,
            color: button.color,
            borderColor: button.backgroundColor,
            cursor: "pointer",
            height: "31px",
          }}
          className="buttons-legend"
        >
          <img src={clickIcon} alt="Info Icon" style={{ marginRight: "5px" }} height={18} width={"auto"} />
          {button.value}
        </Button>
      </OverlayTrigger>
    );
  });

  const bulbData = [
    { color: customSecondaryColor, info: t("Pokaż podpowiedzi"), value: mapSettingsFromRedux?.showLineNumbers },
    { color: primaryColor, info: t("Pokaż odchylenia na mapie"), value: mapSettingsFromRedux?.showDeviations },
    { color: tealColor, info: t("Kolorowa mapa"), value: mapSettingsFromRedux?.colorMap },
    { color: successColor, info: t("Wszystkie_trasy"), value: mapSettingsFromRedux?.showAllRoutes },
    ...(configAgencyName === ("PoznanPKS" as string)
      ? [{ color: warningColor, info: t("Techniczne_pojazdy"), value: mapSettingsFromRedux?.showTechnicalVehicles }]
      : []),
  ];

  const bulbIndicators: JSX.Element[] = bulbData.map((bulb) => {
    return (
      <BulbDiv key={bulb.info}>
        <OverlayTrigger
          trigger={["click", "focus", "hover"]}
          key={bulb.info}
          placement={"bottom"}
          overlay={<Tooltip id={`bulbIndicator-${bulb.info}`}>{bulb.info}</Tooltip>}
        >
          <span>
            <Bulb size={7} key={bulb.info} color={bulb.value ? bulb.color : textColor} />
          </span>
        </OverlayTrigger>
      </BulbDiv>
    );
  });

  return (
    <React.Fragment>
      {currentCalendarState && busesFromRedux && Object.keys(busesFromRedux).length >= 1 ? (
        <div className="ms-auto" style={{ marginRight: "-1rem" }}>
          <ButtonGroup aria-label="Legend Buttons">{legendButtons}</ButtonGroup>
        </div>
      ) : null}
      <BulbContainer>{mapSettingsFromRedux?.showControls ? bulbIndicators : null}</BulbContainer>
    </React.Fragment>
  );
};

export default ButtonsLegend;
